import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { TrendingUp, Users, MousePointerClick, Sparkles } from "lucide-react";

const WINDOW_OPTIONS = [
  { value: 7,  label: "Last 7 days",  weeks: 8 },
  { value: 30, label: "Last 30 days", weeks: 12 },
  { value: 90, label: "Last 90 days", weeks: 16 },
];

// Tiny inline sparkline (no external deps)
function Sparkline({ values, color = "#C05C42", width = 80, height = 22 }) {
  if (!values || values.length === 0) return <span className="text-xs text-[#9A8F7E]">—</span>;
  const max = Math.max(1, ...values);
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => `${i * stepX},${height - (v / max) * (height - 2) - 1}`).join(" ");
  const trend = values.length >= 2 ? (values[values.length - 1] - values[0]) : 0;
  const trendColor = trend > 0 ? "#6A8C5D" : trend < 0 ? "#8F3A3A" : "#9A8F7E";
  return (
    <span className="inline-flex items-center gap-2" data-testid="sparkline">
      <svg width={width} height={height} className="overflow-visible">
        <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
      </svg>
      <span className="text-[10px] uppercase tracking-widest" style={{ color: trendColor }}>
        {trend > 0 ? "↑" : trend < 0 ? "↓" : "→"}
      </span>
    </span>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    const window = WINDOW_OPTIONS.find((w) => w.value === days) || WINDOW_OPTIONS[1];
    Promise.all([
      api.get(`/admin/massey-conversion?days=${days}`),
      api.get(`/admin/massey-conversion-trend?weeks=${window.weeks}`),
    ]).then(([f, t]) => {
      setData(f.data);
      const map = {};
      (t.data?.by_product || []).forEach((row) => { map[row.product_id] = row; });
      setTrends(map);
    }).catch(() => setData(null)).finally(() => setBusy(false));
  }, [days]);

  const t = data?.totals;
  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="label-eyebrow mb-2">Admin · Analytics</p>
          <h1 className="font-serif text-4xl text-[#2D322B] leading-tight">Massey → Apothecary funnel.</h1>
          <p className="text-sm text-[#5C6359] mt-2 max-w-xl">How often Massey is recommending products, and how often members click through.</p>
        </div>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} data-testid="analytics-window" className="bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-1 text-sm text-[#2D322B] outline-none">
          {WINDOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {busy && !data && <p className="text-[#9A8F7E]">Loading…</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8" data-testid="analytics-totals">
            <Stat icon={Sparkles}             label="Mentions"          value={t.mentions} />
            <Stat icon={Users}                label="Unique members"    value={t.unique_members_recommended_to} />
            <Stat icon={MousePointerClick}    label="Attributed clicks" value={t.attributed_clicks} />
            <Stat icon={TrendingUp}            label="Click-through"     value={`${t.overall_ctr_pct}%`} />
          </div>

          <div className="bg-white border border-[#E8DFD0] overflow-x-auto">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E8DFD0] bg-[#F1ECE4] min-w-[760px]">
              <p className="label-eyebrow col-span-4">Product</p>
              <p className="label-eyebrow col-span-2">Weekly trend</p>
              <p className="label-eyebrow col-span-2 text-right">Mentions</p>
              <p className="label-eyebrow col-span-1 text-right">Members</p>
              <p className="label-eyebrow col-span-2 text-right">Clicks</p>
              <p className="label-eyebrow col-span-1 text-right">CTR</p>
            </div>
            {data.by_product.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-[#9A8F7E]" data-testid="analytics-empty">
                Massey hasn&apos;t recommended any products in this window yet.
              </div>
            ) : (
              data.by_product.map((row) => {
                const tr = trends[row.product_id];
                return (
                  <div key={row.product_id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E8DFD0] last:border-b-0 items-center min-w-[760px]" data-testid={`analytics-row-${row.product_id}`}>
                    <div className="col-span-4">
                      <p className="font-serif text-base text-[#2D322B]">{row.product_name}</p>
                      <p className="text-xs text-[#9A8F7E] mt-1">{row.product_id}</p>
                    </div>
                    <div className="col-span-2">
                      <Sparkline values={tr?.mentions_series || []} />
                    </div>
                    <p className="col-span-2 text-right text-[#2D322B]">{row.mentions}</p>
                    <p className="col-span-1 text-right text-[#5C6359]">{row.unique_members_recommended_to}</p>
                    <p className="col-span-2 text-right text-[#2D322B]">{row.attributed_clicks}</p>
                    <p className="col-span-1 text-right text-[#C05C42] font-medium">{row.ctr_pct}%</p>
                  </div>
                );
              })
            )}
          </div>

          <p className="text-xs text-[#9A8F7E] mt-6 leading-relaxed max-w-2xl">
            Weekly trend shows Massey&apos;s mentions of each product, week-over-week. ↑/↓/→ indicates whether the most recent week is up, down, or flat versus the start of the period.
          </p>
        </>
      )}
    </AdminLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#E8DFD0] p-5">
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-4 h-4 text-[#C05C42]" strokeWidth={1.5} />
        <p className="label-eyebrow">{label}</p>
      </div>
      <p className="font-serif text-3xl text-[#2D322B]">{value}</p>
    </div>
  );
}
